'use client';

import { useEffect } from 'react';

/**
 * Global chunk loading error handler component
 * This component adds event listeners to handle chunk loading failures
 * and automatically attempts to reload the page when they occur
 */
export function ChunkErrorHandler() {
  useEffect(() => {
    const handleChunkError = (event: ErrorEvent) => {
      const error = event.error;
      
      // Check if this is a chunk loading error
      if (
        error &&
        (error.name === 'ChunkLoadError' ||
         error.message?.includes('Loading chunk') ||
         error.message?.includes('ChunkLoadError') ||
         event.filename?.includes('_next/static/chunks/'))
      ) {
        console.warn('Chunk loading error detected, attempting to reload:', error);
        
        // Reload the page to retry loading the chunks
        // Use setTimeout to prevent immediate reload loops
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      
      // Check if this is a chunk loading error in a promise
      if (
        error &&
        (error.name === 'ChunkLoadError' ||
         error.message?.includes('Loading chunk') ||
         error.message?.includes('ChunkLoadError'))
      ) {
        console.warn('Chunk loading promise rejection detected, attempting to reload:', error);
        
        // Prevent the error from being logged as unhandled
        event.preventDefault();
        
        // Reload the page to retry loading the chunks
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }

      // Ignore benign Google Maps loader errors (seen on some in-app browsers like LinkedIn iOS)
      if (typeof error?.message === 'string' && error.message.includes('Could not load "util"')) {
        console.warn('Ignoring benign Google Maps loader error:', error);
        event.preventDefault();
      }
    };

    // Add event listeners for both error types
    window.addEventListener('error', handleChunkError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Cleanup function
    return () => {
      window.removeEventListener('error', handleChunkError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // This component doesn't render anything
  return null;
} 